import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import ActivityAttachments from '../../components/activity/ActivityAttachments';
import ActivityIntroScreen from '../../components/activity/ActivityIntroScreen';
import ActivityQuestionCard from '../../components/activity/ActivityQuestionCard';
import ActivitySubmittedBanner from '../../components/activity/ActivitySubmittedBanner';
import SubmitSuccessCelebration from '../../components/activity/SubmitSuccessCelebration';
import { OverallFeedbackSection } from '../../components/SubmissionFeedback';
import { BackNavLink } from '../../components/NavButtons';
import { getActivityStarted, setActivityStarted } from '../../utils/activityStart';
import { getAnswerFile, isQuestionAnswered } from '../../utils/questionUtils';
import {
  getStudentChallengeById,
  submitStudentChallenge,
} from '../../services/challengeService';
import { getClassTitle, getSubjectTitle } from '../../utils/classSubjectUtils';
import { focusTheme } from '../../utils/focusTheme';

const ACTIVITY_SLUG = 'challenges';

function formatChallengeDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'long',
  }).format(date);
}

function getSubmissionAnswers(challenge) {
  return (
    challenge?.submission?.answers ||
    challenge?.submission?.challengeAnswers ||
    challenge?.answers ||
    []
  );
}

function findExistingAnswer(question, challenge) {
  const submissionAnswer = getSubmissionAnswers(challenge).find(
    (answer) => answer.questionId === question.id || answer.question?.id === question.id
  );

  return question.answer || submissionAnswer || null;
}

function getAnswerValue(answer, key) {
  if (!answer) {
    return '';
  }

  if (key === 'selectedOptionId') {
    return (
      answer.selectedOptionId ||
      answer.optionId ||
      answer.selectedOption?.id ||
      answer.option?.id ||
      ''
    );
  }

  return answer.textAnswer || answer.answerText || answer.text || '';
}

function buildInitialAnswers(challenge) {
  return (challenge?.questions || []).reduce((acc, question) => {
    const answer = findExistingAnswer(question, challenge);

    acc[question.id] = {
      selectedOptionId: getAnswerValue(answer, 'selectedOptionId'),
      textAnswer: getAnswerValue(answer, 'textAnswer'),
      file: getAnswerFile(answer),
    };

    return acc;
  }, {});
}

function ChallengeDetailsPage() {
  const { challengeId } = useParams();
  const [challenge, setChallenge] = useState(null);
  const [answers, setAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [showSuccessCelebration, setShowSuccessCelebration] = useState(false);

  const questions = useMemo(
    () => [...(challenge?.questions || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    [challenge]
  );

  const answeredCount = useMemo(
    () => questions.filter((question) => isQuestionAnswered(question, answers[question.id])).length,
    [answers, questions]
  );

  const isSubmitted = Boolean(challenge?.submission);

  useEffect(() => {
    let isMounted = true;

    async function loadChallenge() {
      setIsLoading(true);
      setErrorMessage('');
      setActionMessage('');

      try {
        const challengeData = await getStudentChallengeById(challengeId);

        if (isMounted) {
          setChallenge(challengeData);
          setAnswers(buildInitialAnswers(challengeData));
          setHasStarted(
            Boolean(challengeData?.submission) || getActivityStarted(ACTIVITY_SLUG, challengeId)
          );
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت چالش وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadChallenge();

    return () => {
      isMounted = false;
    };
  }, [challengeId]);

  function handleStartChallenge() {
    setActivityStarted(ACTIVITY_SLUG, challengeId);
    setHasStarted(true);
    setActionMessage('چالش شروع شد. حالا می‌تونی به سوالات پاسخ بدی.');
  }

  function updateAnswer(questionId, value) {
    if (isSubmitted) {
      return;
    }

    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: {
        selectedOptionId: currentAnswers[questionId]?.selectedOptionId || '',
        textAnswer: currentAnswers[questionId]?.textAnswer || '',
        file: currentAnswers[questionId]?.file || null,
        ...value,
      },
    }));
  }

  function buildAnswersPayload() {
    return questions.map((question) => {
      const answer = answers[question.id] || {};

      return {
        questionId: question.id,
        selectedOptionId: question.type === 'multiple_choice' ? answer.selectedOptionId || null : null,
        textAnswer: question.type === 'descriptive' ? (answer.textAnswer || '').trim() || null : null,
      };
    });
  }

  async function refreshChallenge() {
    const challengeData = await getStudentChallengeById(challengeId);
    setChallenge(challengeData);
    setAnswers(buildInitialAnswers(challengeData));
  }

  async function handleSubmitChallenge() {
    const unansweredQuestions = questions.filter(
      (question) => !isQuestionAnswered(question, answers[question.id])
    );

    if (unansweredQuestions.length > 0) {
      setErrorMessage('برای ثبت نهایی، لطفا به همه سوالات پاسخ بده.');
      setActionMessage('');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setActionMessage('');

    try {
      await submitStudentChallenge(challengeId, buildAnswersPayload());
      setActionMessage('چالش با موفقیت ثبت شد.');
      setShowSuccessCelebration(true);
      await refreshChallenge();
    } catch (error) {
      setErrorMessage(error.message || 'ثبت چالش انجام نشد.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <BackNavLink to="/challenges">بازگشت به چالش‌ها</BackNavLink>
        <div className="rounded-[14px] bg-white p-6 text-center text-[12px] text-slate-500 shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          در حال دریافت سوالات چالش...
        </div>
      </div>
    );
  }

  if (errorMessage && !challenge) {
    return (
      <div className="space-y-4">
        <BackNavLink to="/challenges">بازگشت به چالش‌ها</BackNavLink>
        <div className="rounded-[14px] bg-white p-5 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          <p className={`text-[13px] font-bold ${focusTheme.message.error}`}>{errorMessage}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className={`mt-3 rounded-[6px] px-5 py-1 text-[12px] font-bold ${focusTheme.button.retry}`}
          >
            تلاش دوباره
          </button>
        </div>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="space-y-4">
        <BackNavLink to="/challenges">بازگشت به چالش‌ها</BackNavLink>
        <div className="rounded-[14px] bg-white p-5 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
          <p className="text-[15px] font-bold text-slate-800">چالش پیدا نشد</p>
          <p className="mt-2 text-[12px] text-slate-500">این چالش در دسترس نیست.</p>
        </div>
      </div>
    );
  }

  if (!hasStarted) {
    return (
      <ActivityIntroScreen
        attachments={challenge.attachments}
        attachmentsTitle="فایل‌های پیوست چالش"
        backLabel="بازگشت به چالش‌ها"
        backTo="/challenges"
        imageSrc="/images/challange.webp"
        onStart={handleStartChallenge}
        startLabel="شروع چالش"
        stats={[
          {
            label: 'تعداد سوالات',
            value: `${questions.length} سوال`,
            background: focusTheme.stat.remaining,
          },
          {
            label: 'مهلت تحویل',
            value: formatChallengeDate(challenge.dueAt),
            background: focusTheme.stat.completed,
          },
          {
            label: 'کلاس',
            value: getClassTitle(challenge),
            background: focusTheme.stat.remaining,
          },
          {
            label: 'درس',
            value: getSubjectTitle(challenge, 'نامشخص'),
            background: focusTheme.stat.completed,
          },
        ]}
        subtitle="بعد از شروع چالش، سوالات نمایش داده می‌شوند. بعد از پاسخ به همه سوالات، ثبت نهایی را بزن."
        title={challenge.title || 'چالش'}
      />
    );
  }

  return (
    <div className="space-y-5">
      <SubmitSuccessCelebration
        open={showSuccessCelebration}
        onClose={() => setShowSuccessCelebration(false)}
        title="آفرین!"
        description="چالشت با موفقیت ثبت شد. منتظر بازخورد معلم باش."
      />
      <BackNavLink to="/challenges">بازگشت به چالش‌ها</BackNavLink>

      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-2">
          <p
            style={{ background: focusTheme.header.background, boxShadow: focusTheme.header.boxShadow }}
            className="px-6 py-1 rounded-[10px] text-[15px] text-white"
          >
            {challenge.title || 'چالش'}
          </p>
          <img className="h-[50px]" src="/images/challange.webp" alt="" />
        </div>

        <ActivityAttachments
          attachments={challenge.attachments}
          title="فایل‌های پیوست چالش"
        />

        <div
          className="rounded-[8px] px-3 py-2 text-slate-700"
          style={{ background: focusTheme.sectionBg }}
        >
          <p className="text-[13px] font-medium text-slate-800">پیشرفت پاسخ‌دهی</p>
          <p className="mt-1 text-[15px] font-bold text-slate-900">
            {answeredCount} از {questions.length} سوال
          </p>
        </div>

        {isSubmitted && (
          <ActivitySubmittedBanner
            title="چالش ثبت شده است"
            description="پاسخ‌های شما با موفقیت ثبت نهایی شده‌اند."
          />
        )}

        <div
          className="flex flex-col gap-2 p-2 rounded-[8px]"
          style={{ background: focusTheme.sectionBg }}
        >
          {questions.length === 0 ? (
            <div className="rounded-[8px] bg-white px-4 py-6 text-center">
              <p className="text-[13px] font-bold text-slate-800">سوالی برای این چالش ثبت نشده است</p>
            </div>
          ) : (
            questions.map((question, index) => (
              <ActivityQuestionCard
                key={question.id}
                answer={answers[question.id]}
                canAnswer={!isSubmitted}
                entityId={challengeId}
                index={index}
                onChange={updateAnswer}
                question={question}
                slug="challenges"
              />
            ))
          )}
        </div>

        <OverallFeedbackSection submission={challenge.submission} accentColor={focusTheme.accent} />

        {(errorMessage || actionMessage) && (
          <div className="rounded-[10px] bg-white p-3 text-center shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
            {errorMessage && <p className={`text-[12px] font-bold ${focusTheme.message.error}`}>{errorMessage}</p>}
            {actionMessage && <p className={`text-[12px] font-bold ${focusTheme.message.success}`}>{actionMessage}</p>}
          </div>
        )}

        {isSubmitted ? (
          <div className="w-full mx-auto flex flex-col gap-2 items-center justify-center">
            <p className="text-[13px] font-medium text-slate-700">
              پاسخ‌های این چالش ثبت نهایی شده‌اند. منتظر بازخورد معلم باش.
            </p>
          </div>
        ) : (
          <div className="w-full mx-auto flex flex-col gap-2 items-center justify-center">
            <p className="text-[13px] font-medium text-slate-800">
              بعد از کامل شدن همه سوالات، چالش رو نهایی ثبت کن.
            </p>

            <button
              type="button"
              onClick={handleSubmitChallenge}
              disabled={isSubmitting || questions.length === 0}
              className={`w-11/12 mx-auto text-[15px] font-bold py-2.5 text-center flex items-center justify-center rounded-[8px] disabled:opacity-60 ${focusTheme.button.submit}`}
            >
              {isSubmitting ? 'در حال ثبت...' : 'ثبت نهایی چالش'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChallengeDetailsPage;
